import React, { useState, useEffect } from "react";
import { CheckIcon, PlusIcon, MapPinIcon } from "lucide-react";
import GenericDropdown from "./GenericSelection";
import { useGetAllLocations } from "../../queries/admin";
import { useLocationStore } from "../../store/useLocationStore";
import { useDashboard } from "../dashboard/hooks/useDashboard";
import AddLocationModal from "../../components/modal/AddLocationModal";
import { useAuthStore } from "../../store/useAuthStore";
import { getPreSignedUrlForDownload } from "../../api/admin";

export interface Location {
  id: string;
  name: string;
  imageKey?: string;
  planKey?: string;
  image?: string; // Fallback field name
}

interface LocationDisplayProps {
  location: Location;
  showCheckIcon?: boolean;
}

const LocationDisplay: React.FC<LocationDisplayProps> = ({
  location,
  showCheckIcon = false,
}) => {
  const [imageUrl, setImageUrl] = useState<string>();

  useEffect(() => {
    // Check for both imageKey and image fields
    const imageKey = location.imageKey || location.image;
    if (!imageKey) {
      return;
    }
    const fetchUrl = async () => {
      try {
        const res = await getPreSignedUrlForDownload({ key: imageKey });
        setImageUrl(res.url);
      } catch (error) {
        console.error("Failed to fetch location image:", error);
      }
    };

    fetchUrl();
  }, [location.imageKey, location.image]);

  return (
    <div className="flex items-center gap-2">
      {imageUrl ? (
        <img
          className="w-6 h-6 rounded object-cover"
          src={imageUrl}
          alt={`${location.name} location`}
        />
      ) : (
        <MapPinIcon className="w-6 h-6 text-gray-400" />
      )}
      <span className="text-sm">{location.name}</span>
      {showCheckIcon && <CheckIcon className="absolute right-2 h-5 w-5" />}
    </div>
  );
};

const LocationSelection: React.FC = () => {
  const { selectedLocationId, setSelectedLocation } = useLocationStore();
  const { data: locations, isLoading: locationsLoading } = useGetAllLocations();
  const { isEditMode } = useDashboard();
  const { role } = useAuthStore();
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);

  const isAdmin = true;
  // role?.toUpperCase() === "ADMIN";

  const handleSelect = (selectedLocation: Location) => {
    setSelectedLocation(selectedLocation.id, selectedLocation.name);
  };

  const renderLocation = (location: Location, isSelected: boolean) => (
    <LocationDisplay location={location} showCheckIcon={isSelected} />
  );

  // Use locations directly without adding "not available" option
  const locationOptions = React.useMemo(() => {
    return locations || [];
  }, [locations]);

  // Auto-select first location when none is selected, or when the persisted
  // selection no longer exists in the available options (stale store).
  useEffect(() => {
    if (!locationOptions || locationOptions.length === 0) return;
    const isValid = locationOptions.some((loc) => loc.id === selectedLocationId);
    if (!isValid) {
      const first = locationOptions[0];
      setSelectedLocation(first.id, first.name);
    }
  }, [locationOptions, selectedLocationId, setSelectedLocation]);

  return (
    <>
      <div className="flex items-center">
        {locationsLoading ? (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Location
            </label>
            <div className="w-64 h-10 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-sm flex items-center">
              Loading locations...
            </div>
          </div>
        ) : locationOptions && locationOptions.length > 0 ? (
          <div className="flex items-center space-x-1">
            <GenericDropdown<Location>
              options={locationOptions}
              onSelect={handleSelect}
              initialSelectedId={selectedLocationId}
              renderOption={renderLocation}
              buttonClassName="w-64"
              label="Location"
              compact
            />
            {/* {isEditMode && isAdmin && (
              <button
                onClick={() => setShowAddLocationModal(true)}
                className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center justify-center transition-colors duration-200"
                title="Add Location"
              >
                <PlusIcon className="h-3 w-3" />
              </button>
            )} */}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Location
            </label>
            <div className="w-64 h-10 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-sm flex items-center">
              No locations
            </div>
            {/* {isEditMode && isAdmin && (
              <button
                onClick={() => setShowAddLocationModal(true)}
                className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center justify-center transition-colors duration-200"
                title="Add Location"
              >
                <PlusIcon className="h-3 w-3" />
              </button>
            )} */}
          </div>
        )}
      </div>

      {/* Add Location Modal */}
      {showAddLocationModal && (
        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => setShowAddLocationModal(false)}
        />
      )}
    </>
  );
};

export default LocationSelection;
